/**********************************************************************
 * Modale creazione / modifica preroll – mobile-friendly + nuovi colori
 *********************************************************************/
import React, { useState, useEffect } from 'react';
import Modal        from '../common/Modal';
import Loader       from '../Utils/Loader';
import api          from '../../services/api';
import { toBase64 } from '../Utils/file';
import '../../css/prerollFormModal.css';

/* helper → value “YYYY-MM-DDTHH:mm” per <input datetime-local> */
const toInputDT = v =>
  !v
    ? ''
    : new Date(new Date(v).getTime() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

const EMPTY = {
  startTime: '',
  endTime: '',
  type: 'base',
  prio: 0,
  mediaBase64: '',
  originalFilename: '',
  coverBase64: '',
  mediaType: 'audio',
};

export default function PrerollsFormModal({
  open,
  onClose,
  defaultType,
  editing,
  onSaved,
}) {
  const [data, setData] = useState(EMPTY);
  const [coverPrev, setCoverPrev] = useState(null);
  const [busy, setBusy] = useState({ media: false, cover: false });

  const isEdit = Boolean(editing);
  const uploading = busy.media || busy.cover;

  /* bootstrap / reset */
  useEffect(() => {
    if (isEdit && editing) {
      setData({
        ...EMPTY,
        ...editing,
        startTime: toInputDT(editing.startTime),
        endTime: toInputDT(editing.endTime),
        mediaType: editing.mediaType,
      });
      setCoverPrev(editing.coverUrl ?? null);
    } else {
      setData({ ...EMPTY, mediaType: defaultType });
      setCoverPrev(null);
    }
  }, [isEdit, editing, defaultType]);

  /* handlers */
  const handleChange = (e) =>
    setData({ ...data, [e.target.name]: e.target.value });

  const pickFile = async (e, field, busyKey, previewCb = null) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy((b) => ({ ...b, [busyKey]: true }));

    const b64 = await toBase64(file);
    setData((d) => ({
      ...d,
      [field]: b64,
      ...(field === 'mediaBase64'
        ? { originalFilename: file.name }                       // ⬅️ mantieni il nome così com’è
        : {}),
    }));

    if (previewCb) previewCb(URL.createObjectURL(file));
    setBusy((b) => ({ ...b, [busyKey]: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploading) return;
    const payload = { ...data, mediaType: defaultType };
    try {
      isEdit
        ? await api.put(`/prerolls/${editing.id}`, payload)
        : await api.post('/prerolls', payload);
      onSaved();
      onClose();
    } catch (err) {
      /* eslint-disable no-alert */
      console.error(err);
      alert('Errore salvataggio preroll');
    }
  };

  /* render */
  return (
    <Modal open={open} onClose={onClose}>
      <h3 className="preroll-modal-title">
        {isEdit ? 'Modifica preroll' : `Nuovo preroll ${defaultType}`}
      </h3>

      <form className="rr-form rr-modal-scroll" onSubmit={handleSubmit}>
        {/* date/time ------------------------------------------------ */}
        <label>
          Inizio
          <input
            type="datetime-local"
            name="startTime"
            value={data.startTime}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          Fine
          <input
            type="datetime-local"
            name="endTime"
            value={data.endTime}
            onChange={handleChange}
            required
          />
        </label>

        {/* tipo + prio -------------------------------------------- */}
        <div className="rr-row">
          <label>
            Tipo
            <select name="type" value={data.type} onChange={handleChange}>
              <option value="base">Pubblicità</option>
              <option value="first">Primo</option>
              <option value="last">Ultimo</option>
            </select>
          </label>
          <label>
            Ordine
            <input
              type="number"
              name="prio"
              min="0"
              max="10"
              value={data.prio}
              onChange={handleChange}
            />
          </label>
        </div>

        {/* media principale ---------------------------------------- */}
        <label>
          File&nbsp;
          {defaultType === 'audio' ? 'audio (mp3)' : 'video (mp4)'}
          <input
            type="file"
            accept={defaultType === 'audio' ? 'audio/*' : 'video/*'}
            onChange={(e) => pickFile(e, 'mediaBase64', 'media')}
            required={!isEdit}
          />
        </label>
        {busy.media && <Loader small />}

        {/* cover opzionale (solo audio) ---------------------------- */}
        {defaultType === 'audio' && (
          <label>
            Thumbnail / Cover&nbsp;(opz.)
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                pickFile(e, 'coverBase64', 'cover', setCoverPrev)
              }
            />
            {busy.cover && <Loader small />}
            {coverPrev && !busy.cover && (
              <img
                src={coverPrev}
                alt="preview"
                style={{ maxWidth: 120, marginTop: 6, borderRadius: 4 }}
              />
            )}
          </label>
        )}

        {/* actions ------------------------------------------------- */}
        <div className="rr-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Annulla
          </button>

          <button
            type="submit"
            className="btn-save"
            disabled={uploading}
          >
            {uploading ? <Loader small /> : 'Salva'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
